import { LoadingButton } from "@mui/lab";
import {
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Paper } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  getWorkflowErrorLogs,
  updateHandledStatus,
  getInvoiceBundleFiles,
  clientInvoiceBundleCreateFromAllFiles,
  createAllMissingInvoices,
  createAllMissingPrintedInvoices,
  createAllMissingPrintedInvoicesFiles,
  sendPrintedInvoiceFiles,
  createAllMissingReminders,
  generateAllCollectionReports,
  CreateInvoiceBundleFromFortnox,
  generatePaymentReports,
  emailNotCompletedReports,
  printedInvoiceFlow,
  fortnoxReportBack,
  collectionPayments,
} from "API/swesum";

import MUITable from "Components/UI/Tables/MUITable";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

const SELECTABLE_WORKFLOWS = [
  {
    Name: "Hämta fakturabuntar",
    Function: getInvoiceBundleFiles,
  },
  {
    Name: "Skapa fakturabuntar från filer",
    Function: clientInvoiceBundleCreateFromAllFiles,
  },
  {
    Name: "Skapar fakturabuntar från Fortnox",
    Function: CreateInvoiceBundleFromFortnox,
  },
  {
    Name: "Skapa fakturor från fakturabuntar",
    Function: createAllMissingInvoices,
  },
  {
    Name: "Skapa fakturautskrifter",
    Function: createAllMissingPrintedInvoices,
  },
  {
    Name: "Skapa fakturautskriftsfiler",
    Function: createAllMissingPrintedInvoicesFiles,
  },
  {
    Name: "Skicka fakturautskriftsfiler",
    Function: sendPrintedInvoiceFiles,
  },
  {
    Name: "Skapa påminnelser",
    Function: createAllMissingReminders,
  },
  {
    Name: "Skapa inkassorapporter",
    Function: generateAllCollectionReports,
  },
  {
    Name: "Skapa inbetalnings- och BgMax-rapporter",
    Function: generatePaymentReports,
  },
  {
    Name: "Skicka rapporter",
    Function: emailNotCompletedReports,
  },
  {
    Name: "Kör fakturautskriftsflöde",
    Function: printedInvoiceFlow,
  },
  {
    Name: "Återrapportera till Fortnox",
    Function: fortnoxReportBack,
  },
  {
    Name: "Skapa inkassobetalningsexportfil",
    Function: collectionPayments,
  },
];

const getErrorChip = (error, handled) => {
  const style = {
    display: "flex",
    justifyContent: "center",
    minWidth: "5rem",
    maxWidth: "5rem",
  };

  if (error === 1 && handled === 1) {
    return <Chip label="Error" color="success" sx={style} />;
  }

  if (error === 1 && handled === 0) {
    return <Chip label="Error" color="error" sx={style} />;
  }

  return <Chip label="Info" color="default" sx={style} />;
};

const getUserNameById = (users, userId) => {
  const foundObject = users.find((usr) => usr.Id === userId);
  return foundObject ? foundObject.Username : "";
};

const createTableRows = (logs, users) => {
  return logs?.map((c) => {
    return [
      c.Id,
      c.CreateTime,
      getUserNameById(users, c.AppUserId),
      c.UserAction,
      c.Message,
      c.Errors,
      { Handled: c.Handled, Error: c.Error },
      { Handled: c.Handled, Id: c.Id, Error: c.Error },
    ];
  });
};

export function WorkflowTable({ logs, updateLogs, users }) {
  const { openSuccess, openError } = usePopupNotificationContext();
  const [rows, setRows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState("");
  const [loading, setLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [dateFrom, setDateFrom] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(
    dayjs().endOf("month").format("YYYY-MM-DD")
  );

  const handleSetDateTo = async (value) => {
    setDateTo(value);
    await updateLogs(dateFrom, value);
  };

  const handleSetDateFrom = async (value) => {
    setDateFrom(value);
    await updateLogs(value, dateTo);
  };

  const handleStartedWorkflow = async () => {
    const { Function } = SELECTABLE_WORKFLOWS.find(
      (workflow) => workflow.Name === selectedWorkflow
    );
    setLoading(true);

    try {
      await Function();
      await updateLogs(dateFrom, dateTo);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedWorklow = (event) => {
    setSelectedWorkflow(event.target.value);
  };

  const handleShowErrors = async () => {
    const res = await getWorkflowErrorLogs();

    if (res.status === 200) {
      setShowErrors(true);
      setRows(createTableRows(res.data, users));
    }
  };

  const handleShowAll = async () => {
    setShowErrors(false);
    await updateLogs(dateFrom, dateTo);
  };

  const handleChange = async (event, id) => {
    await updateHandledStatus(id, event.target.checked)
      .then((res) => {
        if (res.status !== 200) {
          openError(
            "Något gick fel, kunde inte uppdatera hanterat-status.",
            7500
          );
          console.error(res);
          return;
        }

        if (showErrors) handleShowErrors();
        else updateLogs(dateFrom, dateTo);
        openSuccess("Uppdaterat Hanterat-status", 1500);
      })
      .catch((error) => {
        openError(
          "Något gick fel, kunde inte uppdatera hanterat-status.",
          7500
        );
        console.error(error);
      });
  };

  useEffect(() => {
    setRows(createTableRows(logs, users));
  }, [logs, users]);

  const columns = [
    {
      name: "Id",
      options: {
        filter: false,
        display: false,
      },
    },
    {
      name: "Skapad",
      options: {
        filter: false,
      },
    },
    {
      name: "Användare",
    },
    {
      name: "Åtgärd",
      options: {
        filter: false,
      },
    },
    {
      name: "Meddelande",
      options: {
        filter: false,
      },
    },
    {
      name: "Error",
      options: {
        filter: false,
      },
    },
    {
      name: "Typ",
      options: {
        filter: false,
        customBodyRender: (value) => {
          const { Handled, Error } = value;
          return getErrorChip(Error, Handled);
        },
      },
    },
    {
      name: "Hanterat",
      options: {
        filter: false,
        customBodyRender: (value) => {
          const { Handled, Id, Error } = value;
          return (
            <Checkbox
              sx={Error === 0 ? { display: "none" } : { display: "" }}
              checked={Handled === 1}
              onChange={(event) => handleChange(event, Id, Error)}
            />
          );
        },
      },
    },
  ];

  const options = {
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    filterType: "dropdown",
    responsive: "standard",
    viewColumns: true,
    downloadOptions: {
      separator: ";",
      filename: "all_systems.csv",
    },
    rowsPerPage: 30,
    rowsPerPageOptions: [30, 100],
    onDownload: (buildHead, buildBody, columns, data) => {
      return "\uFEFF" + buildHead(columns) + buildBody(data);
    },
  };

  return (
    <Paper sx={{ padding: "2rem" }}>
      <FormControl
        sx={{
          marginBottom: "1rem",
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
        }}
        size="small"
      >
        <InputLabel id="demo-select-small-label">Välj arbetsflöde</InputLabel>
        <Select
          sx={{ minWidth: "15.4rem" }}
          labelId="demo-select-small-label"
          id="demo-select-small"
          label="Välj arbetsflöde"
          value={selectedWorkflow}
          onChange={handleSelectedWorklow}
        >
          {SELECTABLE_WORKFLOWS.map((workflow) => {
            const { Name } = workflow;
            return (
              <MenuItem key={Name} value={Name}>
                {Name}
              </MenuItem>
            );
          })}
        </Select>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={handleStartedWorkflow}
        >
          Starta
        </LoadingButton>
      </FormControl>
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <DatePicker
          format="YYYY-MM-DD"
          label="Från"
          value={dayjs(dateFrom)}
          onChange={(val) => handleSetDateFrom(dayjs(val).format("YYYY-MM-DD"))}
          slotProps={{ textField: { size: "small" } }}
        />
        <DatePicker
          format="YYYY-MM-DD"
          label="Till"
          value={dayjs(dateTo)}
          onChange={(val) => handleSetDateTo(dayjs(val).format("YYYY-MM-DD"))}
          slotProps={{ textField: { size: "small" } }}
        />
        <LoadingButton variant="outlined" onClick={handleShowAll}>
          Visa allt
        </LoadingButton>
        <LoadingButton variant="outlined" onClick={handleShowErrors}>
          Visa ej hanterade fel
        </LoadingButton>
      </div>
      <MUITable options={options} data={rows} columns={columns} />
    </Paper>
  );
}
